import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { accessibility, containers, grid, sidebar } from '../../utilities/styles';
import { cardLinks } from '../../utilities/accessibility';
import { dateToMaybeRange, currencyFormatter } from '../../utilities/eventUtilities';
import { arrowBack, arrow } from '../../utilities/icons';
import { EventManager } from '../../utilities/events';
import { handle403s } from '../../utilities/AllInErrorHandling';
export class AllInLocationEvents extends LitElement {
    static properties = {
        restUrl: { type: String },
        nonce: { type: String },
        name: { type: String },
        currentLocation: { type: String },
        locationName: { type: String },
        count: { type: Number },
        perpage: { type: Number },
        paginate: { type: Boolean },
        accessibilities: { type: Array },
        _events: { type: Array, state: true },
        from: { type: String },
    };
    constructor() {
        super();
    }
    async connectedCallback() {
        super.connectedCallback();
        this.eventManager = new EventManager(this);
        await this._fetchAllProvisions();
        await this._fetchEvents();
        cardLinks(this.shadowRoot);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.eventManager.disconnect();
    }
    async firstUpdated() {
        cardLinks(this.shadowRoot);
    }
    _setDocumentTitle(title) {
        document.title = `${title} - All In`;
    }
    render() {
        let main;
        let aside;
        let cards = '';
        if (!this._events) {
            cards = html `<allin-loader loading></allin-loader>`;
        }
        if (this._events && this._events.length) {
            cards = this._events.map(event => {
                //let icons = this._getAccessibilityIcons(event.location.provisions);
                const eventProvisions = this._getEventProvisions(event);
                let icons = this._getAccessibilityIcons(eventProvisions);
                let dateRange = dateToMaybeRange(event.firstInstance, event.lastInstance);
                let locationName = event.location?.name ?? '';
                let locationLink = `listings/locations/${event.location?.id ?? ''}`;
                let priceHigh = event.priceRangeHigh ?? null;
                let priceLow = event.priceRangeLow ?? null;
                let priceString = '';
                if (priceLow && priceHigh === priceLow) {
                    priceString = `${currencyFormatter.format(priceLow)}`;
                }
                else {
                    if (priceLow && !priceHigh) {
                        priceString = `From ${currencyFormatter.format(priceLow)}`;
                    }
                    else if (!priceLow && priceHigh) {
                        priceString = `Up to ${currencyFormatter.format(priceHigh)}`;
                    }
                    else if (priceLow && priceHigh) {
                        priceString = `${currencyFormatter.format(priceLow)} - ${currencyFormatter.format(priceHigh)}`;
                    }
                }
                if (priceString) {
                    priceString = html `<p class="small"><strong>Price:</strong> ${priceString}</p>`;
                }
                else {
                    priceString = html `<p></p>`;
                }
                return html `\n                <div class="bg_white card card--event">\n                    <div class="container container--threetwo">\n                        <img class="${event.photo1?.publicUrl ? '' : 'contain'}" src="${event.photo1?.publicUrl ?? '/wp-content/themes/ace/assets/fallback_new.jpeg'}" alt="${event.photo1?.altText ?? ''}" />\n                        ${event.photo1?.altText?.trim()
                    ? html `<allin-alt-block description="${event.photo1?.altText ?? ''}" uid="alt_photo1_${event.id}"></allin-alt-block>` : ''}\n                    </div>\n                    <div class="card-content">\n                        <a href="/listings/events/${event.id}" class="card_target follow"><h3>${event.name}</h3></a>\n                        <p class="small"><strong>Date:</strong> ${dateRange}</p>\n                        <p class="small"><strong>Location:</strong> <a href="/listings/locations/${event.location?.id ?? ''}" class="follow" >${locationName}</a></p>\n                        ${priceString}\n                        <ul class="accessibility-icons" aria-label="${icons.length} accessibility features">\n                            ${icons}\n                        </ul>\n                        ${arrow}\n                    </div>\n                </div>`;
            });
        }
        else if (this._events && this._events.length === 0) {
            cards = html `<p>No upcoming events found for this location.</p>`;
        }
        if (!this.locationName && this._events && this._events.length) {
            for (let event of this._events) {
                if (event.location?.name) {
                    this.locationName = event.location.name;
                    this._setDocumentTitle(this.locationName);
                    break;
                }
            }
        }
        let buttonText = html `See all events at ${this.locationName} ${arrow}`;
        if (this.paginate) {
            buttonText = html `${arrowBack} Return to ${this.locationName}`;
        }
        return html `\n            ${cards.length ? html `\n                ${this.from ? html `\n                    <a href="#" @click=${this._goBackToEvents} class="return"> ${arrowBack} Go back to Events </a>\n                ` : ''} <h2>Upcoming Events at ${this.locationName}</h2>\n            ` : ''}\n            <div class="grid grid_33 gap cards">    \n                ${cards}\n            </div>\n            ${this.paginate ? html `\n                <allin-pagination\n                    total="${this._total ?? 0}"\n                    currentpage="${1}"\n                    pagesize="${this.pageSize ?? 12}"\n                    name="${this.name}"\n                >\n                </allin-pagination>  ` : ''}\n            <div class="button-group">\n                <a href="#" @click=${this._showAllEvents} class="return">${buttonText}</a>\n\n                ${this.from ? html `\n                    <a href="#" @click=${this._goBackToEvents} class="return"> ${arrowBack} Go back to Events </a>\n                ` : ''}\n            </div>\n        `;
    }
    _showAllEvents(e) {
        e.preventDefault();
        this.eventManager.emit(`allin-location-show-hide-events-${this.name}`, {});
    }
    //New click handler to go back to events after navigating to the event location
    _goBackToEvents(e) {
        e.preventDefault();
        if (this.from) {
            window.location.href = decodeURIComponent(this.from);
        }
    }
    async _fetchAllProvisions() {
        try {
            const response = await fetch(`${this.restUrl}?action=get_all_provisions`, {
                headers: {
                    'AllIn-Nonce': this.nonce,
                    'AllIn-Public': 'public',
                }
            });
            //show logged out modal if session has expired
            if (response.status === 403) {
                handle403s();
                return;
            }
            const data = await response.json();
            if (data) {
                this.allProvisions = data.provisions ?? [];
            }
        }
        catch (error) {
            console.error('Error fetching all provisions:', error);
        }
    }
    async _fetchEvents() {
        // Fetch events based on current filters
        const params = new URLSearchParams();
        if (this._start_date)
            params.append('start_date', this._start_date);
        if (this._end_date)
            params.append('end_date', this._end_date);
        if (this.perpage)
            params.append('perpage', this.perpage);
        if (this.currentLocation)
            params.append('currentLocation', this.currentLocation);
        const now = new Date();
        let date = now.toISOString().split('T')[0];
        params.append('from', date);
        try {
            this.grid?.classList.add('loading');
            const response = await fetch(`${this.restUrl}?action=get_events&${params.toString()}`, {
                headers: {
                    'AllIn-Nonce': this.nonce,
                    'AllIn-Public': 'public',
                }
            });
            //show logged out modal if session has expired
            if (response.status === 403) {
                handle403s();
                return;
            }
            const data = await response.json();
            if (data) {
                this._events = data.data ?? [];
                this._total = data.total ?? 0;
            }
        }
        catch (error) {
            console.error('Error fetching events:', error);
        }
        finally {
            this.grid?.classList.remove('loading');
        }
    }
    _getAccessibilityIcons(provisions, returnAsArray = false) {
        let accessibilityIcons = [];
        let provisionMap = [];
        this.allProvisions?.forEach(prov => {
            provisionMap = [...provisionMap, ...prov.provisions];
        });
        if (provisions && provisions.length) {
            provisions.forEach(prov => {
                let provision = provisionMap.find(p => p.id === prov.standardId);
                if (provision?.filterCategory && !accessibilityIcons.includes(provision?.filterCategory)) {
                    accessibilityIcons.push(provision?.filterCategory);
                }
            });
        }
        accessibilityIcons.sort();
        if (returnAsArray) {
            accessibilityIcons = accessibilityIcons.map(cat => {
                let icon = this.accessibilities?.find(a => a.name === cat);
                if (icon) {
                    return {
                        label: icon.label,
                        icon: icon.icon
                    };
                }
            });
            return accessibilityIcons.filter(icon => icon);
        }
        accessibilityIcons = accessibilityIcons.map(cat => {
            let icon = this.accessibilities?.find(a => a.name === cat);
            if (icon) {
                return html `<li class="icon" title="${icon.label}">\n                    <span class="screen-reader-text">${icon.label} icon</span>\n                    <span aria-hidden="true">\n                        ${unsafeHTML(icon.icon ?? '')}\n                    </span>\n                </li>`;
            }
        });
        accessibilityIcons = accessibilityIcons.filter(icon => icon);
        return accessibilityIcons;
    }
    _setDocumentTitle(title) {
        document.title = `${title} - All In`;
    }
    //New helper function to merge all event instances' provision to the event card on location profile
    _getEventProvisions(event) {
        const provisionMap = new Map();
        event.instances?.forEach(instance => {
            instance.provisions?.forEach(provision => {
                // standardId uniquely identifies the provision type
                if (!provisionMap.has(provision.standardId)) {
                    provisionMap.set(provision.standardId, provision);
                }
            });
        });
        return [...provisionMap.values()];
    }
    static styles = [
        accessibility,
        grid,
        containers,
        sidebar,
        css `
        .bg{
            padding:var(--allin-padding,2rem);
            background-color:var(--allin-bg-color,#ccc);
            display:grid;
            gap:1rem;
        }
        .small{
            font-size:0.875rem;
        }
        h2{
            color:var(--allin-highlight-color,#472961);
        }

        h3,p{
            margin:0;
        }

        .button-group{
            display:flex;
            justify-content:space-between;
            margin-top:2rem;
        }

        .border-top{
            border-top:1px solid var(--allin-highlight-color,#472961);
            padding-top:2rem;
        }
        .button-group button, .return{
            background-color:transparent;
            color:var(--allin-highlight-color,#472961);
            border:0;
            display:flex;
            gap:0.5rem;
            align-items:center;
            text-decoration:underline;
            cursor:pointer;
            transition:color var(--transition) ease;
            font-weight:700;
            font-size:1rem;

        }
        .return{
            padding:0;
            margin-bottom:2rem;
        }
        .sidebar .return a{
            font-size: 1.25rem;
            font-weight: 700;
        }

        .button-group button svg, .return svg{
            transition:transform var(--transition) ease;
        }
        .button-group button:hover, .return:hover{
            text-decoration-thickness:2px;
        }
        
        .button-group button:hover svg, .return:hover svg{
            transform:rotate(15deg);
        }
        
        ul{
            list-style:none;
            padding:0;
            margin:0;
        }

        button.back{
            color:var(--allin-highlight-color,#472961);
            background-color:transparent;
            border:0;
            padding:0;
            font-weight:700;
            display:flex;
            align-items:center;
            gap:0.5rem;
        }
        button.back span{
            display:flex;
            align-items:center;
            justify-content:center;
            width:1rem;
        }
        button.back svg{
            transform:rotate(0);
            transition:transform var(--transition) ease;
            width:1rem;
        }
        button.back:hover{
            text-decoration:underline;
        }

        button.back:hover svg{
            transform:rotate(15deg);
        }
        @media (prefers-reduced-motion: reduce) {
            button.back svg{
                transform: none !important;
            }
        }
        @media (min-width: 1000px) {
            .two_column{
                grid-template-rows:auto auto auto auto auto;
                grid-auto-flow:dense;
                column-gap:4rem;
            }
            aside{ 
                grid-row:1 / -1;
                grid-column:2 / 3;
            }
        }

        .card{
            background-color:#fff;
            display:grid;
            grid-template-rows:auto 1fr;
            img{
                transition:opacity var(--transition) ease;
            }
        }

        .card .card-content>svg{
            transition:transform var(--transition) ease;
            display:block;
            margin-left:auto;
        }

        .card-content{
            padding:1rem;
        }

        .card a{
            color:var(--allin-highlight-color,#472961);
            text-decoration-gap:0.25rem;
            text-decoration-thickness:1px;
            transition:text-decoration-thickness var(--transition) ease;
        }
        .card:hover img{
            opacity:0.9;
        } 
        /* First link hover effect only if second link is NOT hovered */
        .card:hover .card-content:not(:has(a.follow:hover)) > a.card_target {
            text-decoration-thickness: 2px;
        }

        /* Second link hover effect */
        .card-content a.follow:hover {
            text-decoration-thickness: 2px;
        }

        .card:hover .card-content>svg{
            transform:translateX(0.5rem);
        }

        .card-content{
            display:grid;
            grid-template-rows:auto auto auto 1fr auto;
        }

        .accessibility-icons{
            display:flex;
            gap:0.75rem;
            margin:3rem 0;
            flex-wrap:wrap;
        }
        .accessibility-icons li{
            width:1.5rem;
            height:1.5rem;
            path,circle,rect,polygon{
                fill:var(--allin-highlight-color,#472961);
            }

        }

        .loading{
            position:relative;
        }
        .loading::after{
            content:'';
            position:absolute;
            top:50%;
            left:50%;
            transform:translate(-50%,-50%);
            width:3rem;
            height:3rem;
            border:4px solid var(--allin-highlight-color,#472961);
            border-top-color:transparent;
            border-radius:50%;
            animation:spin 1s linear infinite;
        }

        @keyframes spin {
            to { transform: translate(-50%,-50%) rotate(360deg); }
        }

        .return{
            margin-top:1rem;
        }

        .container img.contain{
            object-fit: contain;
        }

        `
    ];
}
